import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Modal, Box, Typography, Button, IconButton, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { useTranslation } from '@/lib';
import { PoGrid } from '@/components/grid/PoGrid';
import { locationService } from '@/modules/locations/api/locations/location.service';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import LoadingBlock from '@/components/feedback/LoadingBlock';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';

interface AddLocationModalProps {
  open: boolean;
  onClose: () => void;
  onAddLocation: (location: LocationModel) => void;
  onRemoveLocation: (locationId: number) => void;
  selectedLocationIds: number[];
}

interface LocationRowData {
  id: number;
  name: string;
  code: string;
  isSelected: boolean;
}

const AddLocationModal: FunctionComponent<AddLocationModalProps> = ({
  open,
  onClose,
  onAddLocation,
  onRemoveLocation,
  selectedLocationIds,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [locations, setLocations] = useState<LocationModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Search server-side, debounced. Previously this pulled the entire location table into
  // memory and filtered client-side; /location/search is scoped to the locations the
  // signed-in user may see, so it is also the endpoint that keeps this list correct.
  useEffect(() => {
    if (!open) return;

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await locationService.getByQuery({ query: searchText.trim() });
        if (isCancelled) return;

        if (response.isSuccess) {
          setLocations(response.payload || []);
          setIsError(false);
        } else {
          setIsError(true);
        }
      } catch {
        if (!isCancelled) setIsError(true);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [open, searchText]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchText('');
      setLocations([]);
      setIsError(false);
    }
  }, [open]);

  // Transform locations to row data with selection state. Filtering happens server-side.
  const rowData: LocationRowData[] = useMemo(() => {
    if (!locations) return [];

    return locations.map((location: LocationModel) => ({
      id: location.dto.id,
      name: location.dto.name,
      code: location.dto.code,
      isSelected: selectedLocationIds.includes(location.dto.id),
    }));
  }, [locations, selectedLocationIds]);

  // Add/Remove button cell renderer
  const ActionButtonRenderer = (params: ICellRendererParams<LocationRowData>) => {
    if (!params.data) return null;

    const isSelected = params.data.isSelected;
    const location = locations?.find((loc: LocationModel) => loc.dto.id === params.data!.id);

    if (!location) return null;

    if (isSelected) {
      return (
        <div className="flex items-center justify-center h-full">
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => onRemoveLocation(location.dto.id)}>
            {t('remove')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full">
        <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => onAddLocation(location)}>
          {t('add')}
        </Button>
      </div>
    );
  };

  const columnDefs: ColDef<LocationRowData>[] = useMemo(() => {
    return [
      {
        headerName: t('location_name'),
        field: 'name',
        sortable: true,
        filter: true,
      },
      {
        headerName: t('location_code'),
        field: 'code',
        sortable: true,
        filter: true,
      },
      {
        headerName: '',
        colId: 'actions',
        cellRenderer: ActionButtonRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ];
  }, [t, locations, onAddLocation, onRemoveLocation]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 max-w-[800px] max-h-[80vh] bg-white dark:bg-gray-800 border-black shadow-2xl p-3 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <Typography variant="h6" component="h2" className="font-semibold">
            {t('add_locations')}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <TextField
            fullWidth
            label={t('search')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search_locations_placeholder') || 'Search locations...'}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>

        {/* Locations Grid */}
        {isError ? (
          <ErrorLoadingDataAlert />
        ) : isLoading ? (
          <LoadingBlock />
        ) : (
          <div className="h-96 w-full">
            <PoGrid
              rowData={rowData}
              colDefs={columnDefs}
              gridOptions={{
                domLayout: 'normal',
                suppressHorizontalScroll: false,
                rowHeight: 40,
                headerHeight: 40,
                noRowsOverlayComponent: () => (
                  <div className="flex items-center justify-center h-full text-gray-500">{t('no_locations_found')}</div>
                ),
              }}
            />
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default AddLocationModal;
